/**
 * Pure client-side mirrors of the Missions / Lair formulas from SPEC.md (v1.39.0).
 * The server is the source of truth — these are only used for previews
 * (chance per hero in the "Send a hero" sheet, live income ticking, labels).
 */

import i18n from '../../../i18n';

const EPS = 1e-9;

const num = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

export const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

/**
 * Success chance of a mission for a hero of the given power.
 *   t = clamp((power − required)/(recommended − required), 0, 1)   (recommended ≤ required → t = 1)
 *   chance = min(1, min + (max − min)·t + successBonus)
 */
export function computeSuccessChance({
  power, requiredPower, recommendedPower, minSuccessChance, maxSuccessChance, successBonus = 0,
}) {
  const p = num(power);
  const req = num(requiredPower);
  const rec = num(recommendedPower);
  const min = num(minSuccessChance);
  const max = num(maxSuccessChance);
  const t = rec <= req ? 1 : clamp((p - req) / (rec - req), 0, 1);
  return clamp(min + (max - min) * t + num(successBonus), 0, 1);
}

/**
 * Mission score of a hero (v1.40.0): 3·(w_s·STR + w_a·AGI + w_i·INT)/(w_s + w_a + w_i).
 * A balanced hero scores its power; compared with required/recommended power.
 */
export function missionScore(mission, hero) {
  const ws = Math.max(0, num(mission?.strWeight, 1));
  const wa = Math.max(0, num(mission?.agiWeight, 1));
  const wi = Math.max(0, num(mission?.intWeight, 1));
  const sum = ws + wa + wi;
  const s = num(hero?.str), a = num(hero?.agi), i = num(hero?.int);
  if (sum <= 0) return s + a + i;
  return Math.round((3 * (ws * s + wa * a + wi * i)) / sum);
}

/** Chance range shown on the mission card: [chance at required power, chance at recommended]. */
export function chanceRange(mission, successBonus = 0) {
  const base = {
    requiredPower: mission.requiredPower,
    recommendedPower: mission.recommendedPower,
    minSuccessChance: mission.minSuccessChance,
    maxSuccessChance: mission.maxSuccessChance,
    successBonus,
  };
  return {
    min: computeSuccessChance({ ...base, power: mission.requiredPower }),
    max: computeSuccessChance({ ...base, power: Math.max(num(mission.recommendedPower), num(mission.requiredPower)) }),
  };
}

/** Mission duration in seconds: duration_minutes·60 × (1 − speedBonus), at least 60 s. */
export function effectiveDurationSeconds(durationMinutes, speedBonus = 0) {
  const secs = num(durationMinutes) * 60 * (1 - num(speedBonus));
  return Math.max(60, Math.round(secs));
}

/** Building bonus at level L: L == 0 ? 0 : min(cap, base + perLevel·(L − 1)); cap null = unlimited. */
export function buildingBonus(level, base, perLevel, cap = null) {
  const L = num(level);
  if (L <= 0) return 0;
  const value = num(base) + num(perLevel) * (L - 1);
  return cap === null || cap === undefined ? value : Math.min(num(cap), value);
}

/**
 * Upgrade cost L → L+1: for each cost row with minLevel ≤ L: ceil(baseAmount × growth^L).
 * resourceId null = coins. EPS guards against float noise (1500 × 1.4² = 2940.0000000000005).
 */
export function upgradeCost(level, costs = []) {
  const L = num(level);
  return costs
    .filter((c) => num(c.minLevel) <= L)
    .map((c) => ({
      resourceId: c.resourceId ?? null,
      amount: Math.ceil(num(c.baseAmount) * Math.pow(num(c.growth, 1.5), L) - EPS),
    }));
}

/**
 * Castle income accrued on the client since the last fetch.
 * capHours null → no cap.
 */
export function projectedIncome(pendingAtFetch, incomePerHour, capHours, elapsedMs) {
  const rate = num(incomePerHour);
  const value = num(pendingAtFetch) + (rate * Math.max(0, num(elapsedMs))) / 3600000;
  const cap = incomeCap(rate, capHours);
  return Math.floor(cap === null ? value : Math.min(cap, value));
}

export function incomeCap(incomePerHour, capHours) {
  if (capHours === null || capHours === undefined) return null;
  return Math.floor(num(incomePerHour) * num(capHours));
}

/** 300 → "5m", 7200 → "2h", 5400 → "1h 30m", 285 → "4m 45s", 45 → "45s". */
export function formatDuration(totalSeconds) {
  const s = Math.max(0, Math.round(num(totalSeconds)));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const t = i18n.t.bind(i18n);
  if (d > 0) return h ? t('duration.d_h', '{{d}}d {{h}}h', { d, h }) : t('duration.d', '{{d}}d', { d });
  if (h > 0) return m ? t('duration.h_m', '{{h}}h {{m}}m', { h, m }) : t('duration.h', '{{h}}h', { h });
  if (m > 0) return sec ? t('duration.m_s', '{{m}}m {{s}}s', { m, s: sec }) : t('duration.m', '{{m}}m', { m });
  return t('duration.s', '{{s}}s', { s: sec });
}

/** Countdown "HH:MM:SS" (or "MM:SS" under an hour). */
export function formatCountdown(ms) {
  const total = Math.max(0, Math.ceil(num(ms) / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (v) => String(v).padStart(2, '0');
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

/** Fraction → percent string: 0.95 → "95%", 0.025 → "2.5%". */
export function formatPercent(fraction) {
  const v = Math.round(num(fraction) * 1000) / 10;
  return `${Number.isInteger(v) ? v : v.toFixed(1)}%`;
}

/** 1234 → "1,234", 12500 → "12.5K", 3400000 → "3.4M". */
export function formatAmount(value) {
  const v = num(value);
  const abs = Math.abs(v);
  const trim = (x) => String(Math.round(x * 10) / 10);
  if (abs >= 1e9) return `${trim(v / 1e9)}B`;
  if (abs >= 1e6) return `${trim(v / 1e6)}M`;
  if (abs >= 1e4) return `${trim(v / 1e3)}K`;
  return Math.floor(v).toLocaleString('en-US');
}

/** Human readable bonus value per building bonus type. */
export function formatBonus(bonusType, value) {
  const v = num(value);
  switch (bonusType) {
    case 'COIN_INCOME':     return `+${formatAmount(v)} coins/h`;
    case 'MISSION_SUCCESS': return `+${formatPercent(v)} mission success`;
    case 'MISSION_SPEED':   return `−${formatPercent(v)} mission time`;
    case 'MERGE_DISCOUNT':  return `−${formatPercent(v)} merge cost`;
    default:                return String(v);
  }
}

/** Short value without the description (for "current → next" rows). */
export function formatBonusShort(bonusType, value) {
  const v = num(value);
  switch (bonusType) {
    case 'COIN_INCOME':     return `${formatAmount(v)}/h`;
    case 'MISSION_SUCCESS': return `+${formatPercent(v)}`;
    case 'MISSION_SPEED':
    case 'MERGE_DISCOUNT':  return `−${formatPercent(v)}`;
    default:                return String(v);
  }
}

/**
 * Parse a server timestamp. LocalDateTime is serialized without a zone ("2026-10-05T12:00:00"),
 * so it's parsed as local time — consistently for serverTime and endsAt, which keeps the
 * serverTime offset correct. Also accepts Jackson's array form [y, M, d, h, m, s, nanos].
 */
export function parseServerTime(value) {
  if (value === null || value === undefined) return NaN;
  if (Array.isArray(value)) {
    const [y, mo = 1, d = 1, h = 0, mi = 0, s = 0, ns = 0] = value;
    return new Date(y, mo - 1, d, h, mi, s, Math.floor(ns / 1e6)).getTime();
  }
  if (typeof value === 'number') return value;
  const t = Date.parse(value);
  return Number.isFinite(t) ? t : NaN;
}

/** Offset to add to Date.now() to get "server now" (in the same parse convention). */
export function serverOffsetMs(serverTime, clientNowMs) {
  const t = parseServerTime(serverTime);
  return Number.isFinite(t) ? t - clientNowMs : 0;
}

// ── v1.47.0: rewards by hero level (↔ LairMath.valueForLevel / missionCoins) ──

/** Value of a per-level table for a hero level: level 1 = first entry, above the table → the last one, < 1 → 0. */
export function levelValue(table, level) {
  const list = Array.isArray(table) ? table : [];
  const lvl = Math.floor(Number(level) || 0);
  if (lvl < 1 || list.length === 0) return 0;
  return Number(list[Math.min(lvl, list.length) - 1]) || 0;
}

/**
 * Coins a hero brings from a won mission (3★ / successful expedition): mission.coinsByLevel already includes
 * the mission multiplier; the hero's COIN_BONUS perk adds on top (floored like the server).
 */
export function heroMissionCoins(mission, hero, coinBonus = 0) {
  const base = levelValue(mission?.coinsByLevel, Math.max(1, Number(hero?.level) || 1));
  return Math.floor(base * (1 + Math.max(0, Number(coinBonus) || 0)) + 1e-9);
}

/** "850–6 000" range of a per-level table (or a single value). */
export function levelRange(table) {
  const list = (Array.isArray(table) ? table : []).map(Number).filter(Number.isFinite);
  if (list.length === 0) return '0';
  const min = Math.min(...list);
  const max = Math.max(...list);
  return min === max ? formatAmount(min) : `${formatAmount(min)}–${formatAmount(max)}`;
}
